import { OnScreenKeyboard } from '@components/ui/virtual-keyboard';
import useDisclosure from '@hooks/useDisclosure';
import { PrivacyTipOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useValidateDesktopScreen } from '@utils/screen-size';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useRef, useState } from 'react';
import { auth } from '../../api/auth.api';
import { getColor } from '../../assets/theme';
import { AlertState } from '../../context/app-context';
import DialogChangeSecCode from './dialog-change-sec-code';

/**
 * Interface for the response of verifying a security code
 */
export interface VerifySecCodeInterface {
  status: number;
  data: {
    isValid?: boolean;
    user?: string;
    role?: string;
    message?: string;
    error_message?: string;
    errors?: Array<{ error: { remarks: string } }>;
    level?: number;
  };
  message: string;
  buildVersion: string;
  securityCode: string;
}

const cancellReturn = {
  status: 200,
  data: {
    isValid: false,
    message: 'Cancel',
  },
  message: 'Cancel',
  buildVersion: '',
  securityCode: '',
};

const getSecCodeErrorMessage = (res?: VerifySecCodeInterface) => {
  const responseMessage =
    res?.data?.errors?.[0]?.error?.remarks ||
    res?.data?.error_message ||
    res?.data?.message ||
    res?.message;
  const normalizedMessage = responseMessage?.toLowerCase() || '';
  const isPermissionError =
    !!res?.data?.errors?.length ||
    normalizedMessage.includes('permission') ||
    normalizedMessage.includes('privilege') ||
    normalizedMessage.includes('restricted') ||
    normalizedMessage.includes('authorized') ||
    normalizedMessage.includes('access');

  if (isPermissionError) {
    const hasExplicitPermissionMessage =
      normalizedMessage.includes('permission') ||
      normalizedMessage.includes('privilege') ||
      normalizedMessage.includes('restricted') ||
      normalizedMessage.includes('authorized') ||
      normalizedMessage.includes('access');

    return hasExplicitPermissionMessage && responseMessage
      ? responseMessage
      : `Permission denied: ${
          responseMessage ||
          'You do not have the necessary permissions to access this function.'
        }`;
  }

  const hasExplicitInvalidMessage =
    normalizedMessage.includes('incorrect') ||
    normalizedMessage.includes('invalid') ||
    normalizedMessage.includes('wrong');

  return hasExplicitInvalidMessage && responseMessage
    ? responseMessage
    : `${responseMessage ? `${responseMessage}` : 'Please try again.'}`;
};

/**
 * DialogSecCode component
 *
 * This component renders a dialog for entering and verifying a security code.
 * It uses Material-UI components and custom hooks for app context, authentication, and toast notifications.
 */
const DialogSecCode = ({
  isOpenSecCodeDialog,
  closeSecCodeDialog,
  onConfirmSecCode,
  secCodePriv,
  cancelSecCodeDialog,
  toast,
  onCancelSecCode,
  hideNumpad,
}: {
  isOpenSecCodeDialog: boolean;
  closeSecCodeDialog: () => void;
  onConfirmSecCode: ((params: VerifySecCodeInterface) => Promise<void>) | null;
  secCodePriv: string;
  cancelSecCodeDialog: () => void;
  toast: (data: AlertState) => Promise<void>;
  onCancelSecCode?: () => void;
  hideNumpad?: boolean;
}) => {
  const otpInputRef = useRef<HTMLDivElement | null>(null);

  const { white, primary } = getColor();
  const [secCode, setSecCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { isOpen, onClose, onOpen } = useDisclosure();

  const verifySecCode = async (
    secCode: string,
    priv: string,
  ): Promise<VerifySecCodeInterface> => {
    const res = await auth.verifySecCode(secCode, priv);

    if (!res?.status) {
      throw new Error('Server Error');
    }

    return res;
  };

  const focusToInput = () => {
    const inputElement = otpInputRef.current?.querySelector('input');

    inputElement?.focus();
  };

  const handleValidationError = (message: string) => {
    void toast({
      severity: 'error',
      message,
    });

    setSecCode('');

    window.setTimeout(() => {
      focusToInput();
    }, 0);
  };

  /**
   * Handles the confirmation of the security code
   * Verifies the code and shows appropriate toast messages
   */
  const handleConfirm = async () => {
    if (isLoading) return;

    if (secCode.length !== 8) {
      handleValidationError(
        'Please enter your complete 8-digit security code.',
      );
      return;
    }

    const submittedSecCode = secCode;

    setIsLoading(true);

    try {
      const res = await verifySecCode(submittedSecCode, secCodePriv);

      if (!res?.data) {
        throw new Error('Unable to verify security code. Please try again.');
      }

      if (!res.data.isValid) {
        handleValidationError(getSecCodeErrorMessage(res));
        return;
      }

      closeSecCodeDialog();
      setSecCode('');

      /*
       * Do not block the dialog's loading state while
       * waiting for the parent action.
       */
      void onConfirmSecCode?.({
        ...res,
        securityCode: submittedSecCode,
      }).catch((error) => {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to complete the requested action.';

        void toast({
          severity: 'error',
          message,
        });
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to verify security code. Please try again.';

      handleValidationError(message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles changes in the security code input
   * @param {string} value - The new value of the security code
   */
  const handleChange = (value: any) => {
    setSecCode(value);
  };

  // #region POS CONFIG
  const isDesktopScreen = useValidateDesktopScreen();
  const pathname = window.location.pathname?.split('/')?.[1];
  const isPOS = pathname === 'point-of-sale';
  const keyboard = useRef(null);
  // #endregion

  return (
    <>
      <Dialog open={isOpenSecCodeDialog}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleConfirm();
          }}
        >
          <Stack
            sx={{
              bgcolor: white,
              borderRadius: '4px',
              p: '36px 34px 24px 34px',
              justifyContent: 'flex-start',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <PrivacyTipOutlined
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: '#F7F7FB',
                  p: '12px',
                  borderRadius: '100%',
                  color: primary,
                }}
              />
            </Box>
            <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
              Security Code
            </DialogTitle>
            <DialogContent sx={{ p: '16px 0!important' }}>
              <Typography sx={{ pb: '24px' }}>
                You need to input your security code to proceed.
              </Typography>
              <MuiOtpInput
                ref={otpInputRef}
                autoFocus
                onChange={handleChange}
                value={secCode}
                length={8}
                display='flex'
                flexWrap='wrap'
                alignItems='center'
                justifyContent='center'
                gap={1}
                TextFieldsProps={{
                  size: 'small',
                  type: 'password',
                  required: true,
                  inputMode: 'numeric',
                  sx: {
                    maxWidth: '48px',
                    pb: '16px',
                    '& .MuiInputBase-input': {
                      height: '24px',
                      width: { xs: '24px', md: '48px' },
                      textTransform: 'capitalize',
                      border: `2px solid ${primary}`,
                      borderRadius: '4px',
                    },
                  },
                  placeholder: '#',
                  autoComplete: 'new-password',
                  inputProps: {
                    autoComplete: 'new-password',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    'data-lpignore': true,
                    'data-form-type': 'other',
                  },
                }}
                validateChar={(character, _index) => !isNaN(Number(character))}
              />
              <Button
                type='button'
                sx={{
                  fontSize: '12px',
                  p: 0,
                  ':hover': { bgcolor: 'transparent' },
                }}
                onClick={onOpen}
              >
                Forgot Security Code
              </Button>
              {isPOS && !hideNumpad && isDesktopScreen && (
                <Stack
                  pt={1}
                  sx={{
                    "[data-skbtn='{default}']": {
                      pointerEvents: 'none',
                      opacity: 0.4,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <OnScreenKeyboard
                    keyboardRef={keyboard}
                    type='numericKeyboard'
                    setSecCode={setSecCode}
                    otpInputRef={otpInputRef}
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ p: '0 0 8px 0' }}>
              <Stack direction='row' gap='8px'>
                <Button
                  type='button'
                  sx={{
                    fontSize: '16px',
                    width: '88px',
                  }}
                  onClick={() => {
                    if (isLoading) return;

                    const currentSecCode = secCode;

                    setSecCode('');
                    onCancelSecCode?.();
                    cancelSecCodeDialog();

                    void onConfirmSecCode?.({
                      ...cancellReturn,
                      securityCode: currentSecCode,
                    }).catch((error) => {
                      const message =
                        error instanceof Error
                          ? error.message
                          : 'Unable to cancel the operation.';

                      void toast({
                        severity: 'error',
                        message,
                      });
                    });
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  onClick={() => {
                    setSecCode('');
                    focusToInput();
                  }}
                  disabled={isLoading || secCode.length <= 0}
                >
                  Clear
                </Button>
                <LoadingButton
                  variant='contained'
                  type='submit'
                  loading={isLoading}
                  disabled={secCode.length < 8}
                >
                  Confirm
                </LoadingButton>
              </Stack>
            </DialogActions>
          </Stack>
        </form>
      </Dialog>

      <DialogChangeSecCode {...{ open: isOpen, onClose, propToast: toast }} />
    </>
  );
};

export default DialogSecCode;
